import { lazy, type ComponentType } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { AuthGuard, GuestGuard } from '@/shared/auth/guards';
import { useAuth } from '@/shared/auth/store';
import { homePathForRoles } from '@/shared/auth/role-home';
import { AppShell } from '@/shared/ui/app-shell';
import { legacyRedirectRoutes } from './legacy-redirects';

/**
 * Route table for the Food Sutra ERP.
 *
 * Every page file under `modules/*_/pages` is routed here, and every route is
 * reachable from `shared/nav.ts` or from a link on a page that is. A page with
 * no route is dead code; a route with no nav entry is a 404 waiting to happen
 * the first time somebody clicks through to it.
 */
function RoleHomeRedirect() {
  const roles = useAuth((s) => s.roles);
  return <Navigate to={homePathForRoles(roles)} replace />;
}

/** Auto-reloads once on a chunk-load failure after a deploy. */
function lazyReloadable<T extends { default: ComponentType<any> }>(factory: () => Promise<T>) {
  return lazy(() => factory().catch((err) => {
    const key = 'chunk-reload-once';
    const already = typeof window !== 'undefined' && window.sessionStorage.getItem(key) === '1';
    const isChunkErr = err instanceof Error && /dynamically imported module|Failed to fetch|Loading chunk/i.test(err.message);
    if (isChunkErr && typeof window !== 'undefined' && !already) {
      window.sessionStorage.setItem(key, '1');
      window.location.reload();
      return new Promise<T>(() => {});
    }
    throw err;
  }));
}

if (typeof window !== 'undefined') {
  window.addEventListener('load', () => window.sessionStorage.removeItem('chunk-reload-once'));
}

/* -------------------------------------------------------------------------- */
/* Auth                                                                       */
/* -------------------------------------------------------------------------- */
const LoginPage = lazyReloadable(() => import('@/modules/core/pages/LoginPage'));
const RegisterPage = lazyReloadable(() => import('@/modules/core/pages/RegisterPage'));
const ForgotPage = lazyReloadable(() => import('@/modules/core/pages/ForgotPage'));

/* -------------------------------------------------------------------------- */
/* Core                                                                      */
/* -------------------------------------------------------------------------- */
const DashboardPage = lazyReloadable(() => import('@/modules/core/pages/DashboardPage'));
const ProfilePage = lazyReloadable(() => import('@/modules/core/pages/ProfilePage'));
const NotificationsPage = lazyReloadable(() => import('@/modules/core/pages/NotificationsPage'));
const ApprovalsPage = lazyReloadable(() => import('@/modules/core/pages/ApprovalsPage'));
const HelpTourPage = lazyReloadable(() => import('@/modules/core/pages/HelpTourPage'));
const UsersRolesPage = lazyReloadable(() => import('@/modules/core/pages/UsersRolesPage'));
const UserInvitePage = lazyReloadable(() => import('@/modules/core/pages/UserInvitePage'));
const SessionsPage = lazyReloadable(() => import('@/modules/core/pages/SessionsPage'));
const LoginHistoryPage = lazyReloadable(() => import('@/modules/core/pages/LoginHistoryPage'));
const NumberingSettingsPage = lazyReloadable(() => import('@/modules/core/pages/NumberingSettingsPage'));
const GlobalSettingsPage = lazyReloadable(() => import('@/modules/core/pages/GlobalSettingsPage'));
const TerminologyPage = lazyReloadable(() => import('@/modules/core/pages/TerminologyPage'));
const ImportPage = lazyReloadable(() => import('@/modules/core/pages/ImportPage'));
const AuditLogPage = lazyReloadable(() => import('@/modules/core/pages/AuditLogPage'));
const DemoDataPage = lazyReloadable(() => import('@/modules/core/pages/DemoDataPage'));
const NotFoundPage = lazyReloadable(() => import('@/modules/core/pages/NotFoundPage'));

/* -------------------------------------------------------------------------- */
/* Organization + Master data                                                */
/* -------------------------------------------------------------------------- */
const CompaniesPage = lazyReloadable(() => import('@/modules/organization/pages/CompaniesPage'));
const BranchesPage = lazyReloadable(() => import('@/modules/organization/pages/BranchesPage'));
const DepartmentsPage = lazyReloadable(() => import('@/modules/organization/pages/DepartmentsPage'));
const DepartmentDetailPage = lazyReloadable(() => import('@/modules/organization/pages/DepartmentDetailPage'));
const CustomersPage = lazyReloadable(() => import('@/modules/masterdata/pages/CustomersPage'));
const CustomerDetailPage = lazyReloadable(() => import('@/modules/masterdata/pages/CustomerDetailPage'));
const SuppliersPage = lazyReloadable(() => import('@/modules/masterdata/pages/SuppliersPage'));
const SupplierDetailPage = lazyReloadable(() => import('@/modules/masterdata/pages/SupplierDetailPage'));
const WarehousesPage = lazyReloadable(() => import('@/modules/masterdata/pages/WarehousesPage'));
const WarehouseDetailPage = lazyReloadable(() => import('@/modules/masterdata/pages/WarehouseDetailPage'));
const TaxesPage = lazyReloadable(() => import('@/modules/masterdata/pages/TaxesPage'));
const PaymentTermsPage = lazyReloadable(() => import('@/modules/masterdata/pages/PaymentTermsPage'));
const UomsPage = lazyReloadable(() => import('@/modules/masterdata/pages/UomsPage'));
const CategoriesPage = lazyReloadable(() => import('@/modules/masterdata/pages/CategoriesPage'));
const CurrenciesPage = lazyReloadable(() => import('@/modules/masterdata/pages/CurrenciesPage'));
const TransportersPage = lazyReloadable(() => import('@/modules/masterdata/pages/TransportersPage'));

/* -------------------------------------------------------------------------- */
/* Products + Stores                                                         */
/* -------------------------------------------------------------------------- */
const ProductsPage = lazyReloadable(() => import('@/modules/product/pages/ProductsPage'));
const ProductDetailPage = lazyReloadable(() => import('@/modules/product/pages/ProductDetailPage'));
const ProductDashboard = lazyReloadable(() => import('@/modules/product/pages/ProductDashboard'));
const ProductLabelPage = lazyReloadable(() => import('@/modules/product/pages/ProductLabelPage'));
const ProductPrintSheetPage = lazyReloadable(() => import('@/modules/product/pages/ProductPrintSheetPage'));
const ProductPriceListPrintPage = lazyReloadable(() => import('@/modules/product/pages/ProductPriceListPrintPage'));
const InventoryDashboard = lazyReloadable(() => import('@/modules/inventory/pages/InventoryDashboard'));
const StockBalancesPage = lazyReloadable(() => import('@/modules/inventory/pages/StockBalancesPage'));
const StockMovementsPage = lazyReloadable(() => import('@/modules/inventory/pages/StockMovementsPage'));
const StockTransfersPage = lazyReloadable(() => import('@/modules/inventory/pages/StockTransfersPage'));
const StockTransferFormPage = lazyReloadable(() => import('@/modules/inventory/pages/StockTransferFormPage'));
const StockTransferDetailPage = lazyReloadable(() => import('@/modules/inventory/pages/StockTransferDetailPage'));
const StockAdjustmentsPage = lazyReloadable(() => import('@/modules/inventory/pages/StockAdjustmentsPage'));
const StockAdjustmentFormPage = lazyReloadable(() => import('@/modules/inventory/pages/StockAdjustmentFormPage'));
const StockAdjustmentDetailPage = lazyReloadable(() => import('@/modules/inventory/pages/StockAdjustmentDetailPage'));
const BatchDetailPage = lazyReloadable(() => import('@/modules/inventory/pages/BatchDetailPage'));
const BatchesPage = lazyReloadable(() => import('@/modules/inventory/pages/BatchesPage'));
const StockLedgerPage = lazyReloadable(() => import('@/modules/inventory/pages/StockLedgerPage'));
const StockIssuesPage = lazyReloadable(() => import('@/modules/inventory/pages/StockIssuesPage'));
const StockIssueDetailPage = lazyReloadable(() => import('@/modules/inventory/pages/StockIssueDetailPage'));
const StockCountsPage = lazyReloadable(() => import('@/modules/inventory/pages/StockCountsPage'));
const StockCountDetailPage = lazyReloadable(() => import('@/modules/inventory/pages/StockCountDetailPage'));
const CycleCountPage = lazyReloadable(() => import('@/modules/inventory/pages/CycleCountPage'));
const StockAgingPage = lazyReloadable(() => import('@/modules/inventory/pages/StockAgingPage'));
const ReorderSuggestionsPage = lazyReloadable(() => import('@/modules/inventory/pages/ReorderSuggestionsPage'));

/* -------------------------------------------------------------------------- */
/* Procurement                                                               */
/* -------------------------------------------------------------------------- */
const PurchaseDashboard = lazyReloadable(() => import('@/modules/purchase/pages/PurchaseDashboard'));
const PurchaseRequestsPage = lazyReloadable(() => import('@/modules/purchase/pages/PurchaseRequestsPage'));
const PurchaseRequestFormPage = lazyReloadable(() => import('@/modules/purchase/pages/PurchaseRequestFormPage'));
const PurchaseRequestDetailPage = lazyReloadable(() => import('@/modules/purchase/pages/PurchaseRequestDetailPage'));
const PurchaseOrdersPage = lazyReloadable(() => import('@/modules/purchase/pages/PurchaseOrdersPage'));
const PurchaseOrderFormPage = lazyReloadable(() => import('@/modules/purchase/pages/PurchaseOrderFormPage'));
const PurchaseOrderDetailPage = lazyReloadable(() => import('@/modules/purchase/pages/PurchaseOrderDetailPage'));
const GoodsReceiptsPage = lazyReloadable(() => import('@/modules/purchase/pages/GoodsReceiptsPage'));
const GoodsReceiptDetailPage = lazyReloadable(() => import('@/modules/purchase/pages/GoodsReceiptDetailPage'));
const BillsPage = lazyReloadable(() => import('@/modules/purchase/pages/BillsPage'));
const BillFormPage = lazyReloadable(() => import('@/modules/purchase/pages/BillFormPage'));
const BillDetailPage = lazyReloadable(() => import('@/modules/purchase/pages/BillDetailPage'));
const DebitNotesPage = lazyReloadable(() => import('@/modules/purchase/pages/DebitNotesPage'));
const DebitNoteDetailPage = lazyReloadable(() => import('@/modules/purchase/pages/DebitNoteDetailPage'));
const RfqsPage = lazyReloadable(() => import('@/modules/purchase/pages/RfqsPage'));
const RfqFormPage = lazyReloadable(() => import('@/modules/purchase/pages/RfqFormPage'));
const RfqDetailPage = lazyReloadable(() => import('@/modules/purchase/pages/RfqDetailPage'));
const RfqComparePage = lazyReloadable(() => import('@/modules/purchase/pages/RfqComparePage'));
const SupplierQuotesPage = lazyReloadable(() => import('@/modules/purchase/pages/SupplierQuotesPage'));
const SupplierQuoteFormPage = lazyReloadable(() => import('@/modules/purchase/pages/SupplierQuoteFormPage'));
const SupplierQuoteDetailPage = lazyReloadable(() => import('@/modules/purchase/pages/SupplierQuoteDetailPage'));
const SupplierEvaluationPage = lazyReloadable(() => import('@/modules/purchase/pages/SupplierEvaluationPage'));
const PurchasePrintPage = lazyReloadable(() => import('@/modules/purchase/pages/PurchasePrintPage'));

/* -------------------------------------------------------------------------- */
/* Billing                                                                   */
/* -------------------------------------------------------------------------- */
const BillingDashboardPage = lazyReloadable(() => import('@/modules/billing/pages/BillingDashboardPage'));
const InvoicesPage = lazyReloadable(() => import('@/modules/sales/pages/InvoicesPage'));
// Points straight at the real component. `InvoiceDetailPage.tsx` was a
// one-line re-export shim, so this route pulled two chunks for one page and a
// grep for the invoice detail page returned two files, one of them empty.
const InvoiceDetailPage = lazyReloadable(() => import('@/modules/sales/pages/SalesInvoiceDetailPage'));
const SalesInvoiceFormPage = lazyReloadable(() => import('@/modules/sales/pages/SalesInvoiceFormPage'));
const CreditNotesPage = lazyReloadable(() => import('@/modules/sales/pages/CreditNotesPage'));
const CreditNoteDetailPage = lazyReloadable(() => import('@/modules/sales/pages/CreditNoteDetailPage'));
const ReceiptsPage = lazyReloadable(() => import('@/modules/sales/pages/ReceiptsPage'));
const ReceiptDetailPage = lazyReloadable(() => import('@/modules/sales/pages/ReceiptDetailPage'));
const ReceivablesAgeingPage = lazyReloadable(() => import('@/modules/sales/pages/ReceivablesAgeingPage'));
const CustomerStatementPage = lazyReloadable(() => import('@/modules/sales/pages/CustomerStatementPage'));
// The two billing reports on paper. Neither is a per-document printable, so
// they sit outside `/print/:kind/:id` with their filters on the query string.
const ReceivablesAgeingPrintPage = lazyReloadable(() => import('@/modules/sales/pages/ReceivablesAgeingPrintPage'));
const CustomerStatementPrintPage = lazyReloadable(() => import('@/modules/sales/pages/CustomerStatementPrintPage'));

/* -------------------------------------------------------------------------- */
/* People                                                                    */
/* -------------------------------------------------------------------------- */
const HrDashboard = lazyReloadable(() => import('@/modules/hr/pages/HrDashboard'));
const EmployeesPage = lazyReloadable(() => import('@/modules/hr/pages/EmployeesPage'));
const EmployeeFormPage = lazyReloadable(() => import('@/modules/hr/pages/EmployeeFormPage'));
const EmployeeDetailPage = lazyReloadable(() => import('@/modules/hr/pages/EmployeeDetailPage'));
const EmployeeIdCardPage = lazyReloadable(() => import('@/modules/hr/pages/EmployeeIdCardPage'));
const OrgChartPage = lazyReloadable(() => import('@/modules/hr/pages/OrgChartPage'));
const SelfServicePage = lazyReloadable(() => import('@/modules/hr/pages/SelfServicePage'));
const AdvancesPage = lazyReloadable(() => import('@/modules/hr/pages/AdvancesPage'));
const AdvanceDetailPage = lazyReloadable(() => import('@/modules/hr/pages/AdvanceDetailPage'));
const RecoveryDetailPage = lazyReloadable(() => import('@/modules/hr/pages/RecoveryDetailPage'));
const RecoveriesPage = lazyReloadable(() => import('@/modules/hr/pages/RecoveriesPage'));
const EmployeeExitsPage = lazyReloadable(() => import('@/modules/hr/pages/EmployeeExitsPage'));
const WageRulesPage = lazyReloadable(() => import('@/modules/hr/pages/WageRulesPage'));
const EmployeeExitDetailPage = lazyReloadable(() => import('@/modules/hr/pages/EmployeeExitDetailPage'));
const WageRuleDetailPage = lazyReloadable(() => import('@/modules/hr/pages/WageRuleDetailPage'));
const HrLoansPage = lazyReloadable(() => import('@/modules/hr/pages/LoansPage'));
const HrLoanDetailPage = lazyReloadable(() => import('@/modules/hr/pages/LoanDetailPage'));
const HrIdCardsPage = lazyReloadable(() => import('@/modules/hr/pages/IdCardsPage'));
const HrIdCardDetailPage = lazyReloadable(() => import('@/modules/hr/pages/IdCardDetailPage'));
const HrUniformIssuesPage = lazyReloadable(() => import('@/modules/hr/pages/UniformIssuesPage'));
const HrUniformIssueDetailPage = lazyReloadable(() => import('@/modules/hr/pages/UniformIssueDetailPage'));
const HrUniformItemsPage = lazyReloadable(() => import('@/modules/hr/pages/UniformItemsPage'));
const HrRecoveriesDuePage = lazyReloadable(() => import('@/modules/hr/pages/RecoveriesDuePage'));
const HrPrintPage = lazyReloadable(() => import('@/modules/hr/pages/HrPrintPage'));
const ManpowerBoardPage = lazyReloadable(() => import('@/modules/manpower/pages/ManpowerBoardPage'));
const RequisitionsPage = lazyReloadable(() => import('@/modules/manpower/pages/RequisitionsPage'));
const ReservePoolPage = lazyReloadable(() => import('@/modules/manpower/pages/ReservePoolPage'));
const RequisitionDetailPage = lazyReloadable(() => import('@/modules/manpower/pages/RequisitionDetailPage'));
const ManpowerPrintPage = lazyReloadable(() => import('@/modules/manpower/pages/ManpowerPrintPage'));
const AttendanceDashboard = lazyReloadable(() => import('@/modules/attendance/pages/AttendanceDashboard'));
const AttendanceListPage = lazyReloadable(() => import('@/modules/attendance/pages/AttendanceListPage'));
const AttendanceCalendarPage = lazyReloadable(() => import('@/modules/attendance/pages/AttendanceCalendarPage'));
const LeaveDashboard = lazyReloadable(() => import('@/modules/leave/pages/LeaveDashboard'));
const LeaveApplicationPage = lazyReloadable(() => import('@/modules/leave/pages/LeaveApplicationPage'));
const LeaveApprovalsPage = lazyReloadable(() => import('@/modules/leave/pages/LeaveApprovalsPage'));
const TeamLeaveCalendarPage = lazyReloadable(() => import('@/modules/leave/pages/TeamLeaveCalendarPage'));
const LeaveRequestDetailPage = lazyReloadable(() => import('@/modules/leave/pages/LeaveRequestDetailPage'));
const PayrollDashboard = lazyReloadable(() => import('@/modules/payroll/pages/PayrollDashboard'));
const PayrollRunPage = lazyReloadable(() => import('@/modules/payroll/pages/PayrollRunPage'));
const PayrollRunDetailPage = lazyReloadable(() => import('@/modules/payroll/pages/PayrollRunDetailPage'));
const PayslipsPage = lazyReloadable(() => import('@/modules/payroll/pages/PayslipsPage'));
const PayslipDetailPage = lazyReloadable(() => import('@/modules/payroll/pages/PayslipDetailPage'));
const SalaryStructuresPage = lazyReloadable(() => import('@/modules/payroll/pages/SalaryStructuresPage'));
const SalaryStructureDetailPage = lazyReloadable(() => import('@/modules/payroll/pages/SalaryStructureDetailPage'));
const SalaryComponentsPage = lazyReloadable(() => import('@/modules/payroll/pages/SalaryComponentsPage'));
const SalaryComponentDetailPage = lazyReloadable(() => import('@/modules/payroll/pages/SalaryComponentDetailPage'));
const PayrollPrintPage = lazyReloadable(() => import('@/modules/payroll/pages/PayrollPrintPage'));

/* -------------------------------------------------------------------------- */
/* Money                                                                     */
/* -------------------------------------------------------------------------- */
const AccountingDashboard = lazyReloadable(() => import('@/modules/accounting/pages/AccountingDashboard'));
const ChartOfAccountsPage = lazyReloadable(() => import('@/modules/accounting/pages/ChartOfAccountsPage'));
const AccountLedgerPage = lazyReloadable(() => import('@/modules/accounting/pages/AccountLedgerPage'));
const JournalPage = lazyReloadable(() => import('@/modules/accounting/pages/JournalPage'));
const JournalEntryFormPage = lazyReloadable(() => import('@/modules/accounting/pages/JournalEntryFormPage'));
const JournalEntryDetailPage = lazyReloadable(() => import('@/modules/accounting/pages/JournalEntryDetailPage'));
const TrialBalancePage = lazyReloadable(() => import('@/modules/accounting/pages/TrialBalancePage'));
const BankAccountsPage = lazyReloadable(() => import('@/modules/accounting/pages/BankAccountsPage'));
const BankAccountDetailPage = lazyReloadable(() => import('@/modules/accounting/pages/BankAccountDetailPage'));
const BankReconciliationPage = lazyReloadable(() => import('@/modules/accounting/pages/BankReconciliationPage'));
const BudgetsPage = lazyReloadable(() => import('@/modules/accounting/pages/BudgetsPage'));
const BudgetDetailPage = lazyReloadable(() => import('@/modules/accounting/pages/BudgetDetailPage'));
const BudgetFormPage = lazyReloadable(() => import('@/modules/accounting/pages/BudgetFormPage'));
const ReceiptsPaymentsPage = lazyReloadable(() => import('@/modules/accounting/pages/ReceiptsPaymentsPage'));
const AccountingPrintPage = lazyReloadable(() => import('@/modules/accounting/pages/AccountingPrintPage'));
const TallyExportPage = lazyReloadable(() => import('@/modules/accounting/pages/TallyExportPage'));
const ExpenseDashboard = lazyReloadable(() => import('@/modules/expense/pages/ExpenseDashboard'));
const ExpenseClaimsPage = lazyReloadable(() => import('@/modules/expense/pages/ExpenseClaimsPage'));
const ExpenseClaimFormPage = lazyReloadable(() => import('@/modules/expense/pages/ExpenseClaimFormPage'));
const ExpenseClaimDetailPage = lazyReloadable(() => import('@/modules/expense/pages/ExpenseClaimDetailPage'));
const ExpenseCategoriesPage = lazyReloadable(() => import('@/modules/expense/pages/ExpenseCategoriesPage'));
const ExpenseAdvancesPage = lazyReloadable(() => import('@/modules/expense/pages/ExpenseAdvancesPage'));
const CreditFacilitiesPage = lazyReloadable(() => import('@/modules/finance/pages/CreditFacilitiesPage'));
const CreditFacilityDetailPage = lazyReloadable(() => import('@/modules/finance/pages/CreditFacilityDetailPage'));
const CashFlowPage = lazyReloadable(() => import('@/modules/finance/pages/CashFlowPage'));
const ProfitVsCashPage = lazyReloadable(() => import('@/modules/finance/pages/ProfitVsCashPage'));
const ExpenseAdvanceDetailPage = lazyReloadable(() => import('@/modules/expense/pages/ExpenseAdvanceDetailPage'));
const ExpensePrintPage = lazyReloadable(() => import('@/modules/expense/pages/ExpensePrintPage'));

/* -------------------------------------------------------------------------- */
/* Catering operations                                                       */
/* -------------------------------------------------------------------------- */
const SitesPage = lazyReloadable(() => import('@/modules/sites/pages/SitesPage'));
const SiteDashboardPage = lazyReloadable(() => import('@/modules/sites/pages/SiteDashboardPage'));
const PaxChangesPage = lazyReloadable(() => import('@/modules/sites/pages/PaxChangesPage'));
const MealServicesPage = lazyReloadable(() => import('@/modules/sites/pages/MealServicesPage'));
const ReconciliationsPage = lazyReloadable(() => import('@/modules/sites/pages/ReconciliationsPage'));
const ReconciliationDetailPage = lazyReloadable(() => import('@/modules/sites/pages/ReconciliationDetailPage'));
const MealServiceDetailPage = lazyReloadable(() => import('@/modules/sites/pages/MealServiceDetailPage'));
const PaxChangeDetailPage = lazyReloadable(() => import('@/modules/sites/pages/PaxChangeDetailPage'));
const SitesPrintPage = lazyReloadable(() => import('@/modules/sites/print/SitesPrintPage'));
const RecipesPage = lazyReloadable(() => import('@/modules/kitchen/pages/RecipesPage'));
const RecipeDetailPage = lazyReloadable(() => import('@/modules/kitchen/pages/RecipeDetailPage'));
const MenusPage = lazyReloadable(() => import('@/modules/kitchen/pages/MenusPage'));
const MenuDetailPage = lazyReloadable(() => import('@/modules/kitchen/pages/MenuDetailPage'));
const ProductionPlansPage = lazyReloadable(() => import('@/modules/kitchen/pages/ProductionPlansPage'));
const ProductionPlanDetailPage = lazyReloadable(() => import('@/modules/kitchen/pages/ProductionPlanDetailPage'));
const WastagePage = lazyReloadable(() => import('@/modules/kitchen/pages/WastagePage'));
const WastageDetailPage = lazyReloadable(() => import('@/modules/kitchen/pages/WastageDetailPage'));
const KitchenPrintPage = lazyReloadable(() => import('@/modules/kitchen/pages/KitchenPrintPage'));
const EventsPage = lazyReloadable(() => import('@/modules/events/pages/EventsPage'));
const EventDetailPage = lazyReloadable(() => import('@/modules/events/pages/EventDetailPage'));
const EventClosureBoardPage = lazyReloadable(() => import('@/modules/events/pages/EventClosureBoardPage'));
const EventTypesPage = lazyReloadable(() => import('@/modules/events/pages/EventTypesPage'));
const EventTypeDetailPage = lazyReloadable(() => import('@/modules/events/pages/EventTypeDetailPage'));
const TendersPage = lazyReloadable(() => import('@/modules/tender/pages/TendersPage'));
const TenderDetailPage = lazyReloadable(() => import('@/modules/tender/pages/TenderDetailPage'));
const TenderDashboardPage = lazyReloadable(() => import('@/modules/tender/pages/TenderDashboardPage'));
const TenderMilestonesPage = lazyReloadable(() => import('@/modules/tender/pages/TenderMilestonesPage'));
const TenderDocumentsPage = lazyReloadable(() => import('@/modules/tender/pages/TenderDocumentsPage'));
const TenderCostingDetailPage = lazyReloadable(() => import('@/modules/tender/pages/TenderCostingDetailPage'));
const TenderPrintPage = lazyReloadable(() => import('@/modules/tender/pages/TenderPrintPage'));
const MobilizationsPage = lazyReloadable(() => import('@/modules/mobilization/pages/MobilizationsPage'));
const MobilizationDetailPage = lazyReloadable(() => import('@/modules/mobilization/pages/MobilizationDetailPage'));
const MobilizationPrintPage = lazyReloadable(() => import('@/modules/mobilization/print/MobilizationPrintPage'));
const AssetsPage = lazyReloadable(() => import('@/modules/assets/pages/AssetsPage'));
const AssetDetailPage = lazyReloadable(() => import('@/modules/assets/pages/AssetDetailPage'));
const AssetCategoriesPage = lazyReloadable(() => import('@/modules/assets/pages/AssetCategoriesPage'));
const AssetsPrintPage = lazyReloadable(() => import('@/modules/assets/print/AssetsPrintPage'));

/* -------------------------------------------------------------------------- */
/* Quality, upkeep, contracts                                                */
/* -------------------------------------------------------------------------- */
const QualityDashboard = lazyReloadable(() => import('@/modules/quality/pages/QualityDashboard'));
const InspectionsPage = lazyReloadable(() => import('@/modules/quality/pages/InspectionsPage'));
const InspectionDetailPage = lazyReloadable(() => import('@/modules/quality/pages/InspectionDetailPage'));
const NonConformancesPage = lazyReloadable(() => import('@/modules/quality/pages/NonConformancesPage'));
const NonConformanceDetailPage = lazyReloadable(() => import('@/modules/quality/pages/NonConformanceDetailPage'));
const ComplaintsPage = lazyReloadable(() => import('@/modules/quality/pages/ComplaintsPage'));
const FoodSafetyPage = lazyReloadable(() => import('@/modules/quality/pages/FoodSafetyPage'));
const MaintenanceDashboard = lazyReloadable(() => import('@/modules/maintenance/pages/MaintenanceDashboard'));
const MaintenanceRequestsPage = lazyReloadable(() => import('@/modules/maintenance/pages/MaintenanceRequestsPage'));
const MaintenanceRequestDetailPage = lazyReloadable(() => import('@/modules/maintenance/pages/MaintenanceRequestDetailPage'));
const ContractsPage = lazyReloadable(() => import('@/modules/contract/pages/ContractsPage'));
const ContractDashboard = lazyReloadable(() => import('@/modules/contract/pages/ContractDashboard'));
const ContractDetailPage = lazyReloadable(() => import('@/modules/contract/pages/ContractDetailPage'));
const ContractObligationsPage = lazyReloadable(() => import('@/modules/contract/pages/ContractObligationsPage'));
const ContractPrintPage = lazyReloadable(() => import('@/modules/contract/print/ContractPrintPage'));
const ComplianceDashboard = lazyReloadable(() => import('@/modules/compliance/pages/ComplianceDashboard'));
const ComplianceDocumentsPage = lazyReloadable(() => import('@/modules/compliance/pages/ComplianceDocumentsPage'));
const ComplianceDocumentDetailPage = lazyReloadable(() => import('@/modules/compliance/pages/ComplianceDocumentDetailPage'));

/* -------------------------------------------------------------------------- */
/* Work + management                                                         */
/* -------------------------------------------------------------------------- */
const MyWorkPage = lazyReloadable(() => import('@/modules/tasks/pages/MyWorkPage'));
const TasksPage = lazyReloadable(() => import('@/modules/tasks/pages/TasksPage'));
const TaskDetailPage = lazyReloadable(() => import('@/modules/tasks/pages/TaskDetailPage'));
const ChecklistRunsPage = lazyReloadable(() => import('@/modules/tasks/pages/ChecklistRunsPage'));
const ChecklistRunPage = lazyReloadable(() => import('@/modules/tasks/pages/ChecklistRunPage'));
const TasksPrintPage = lazyReloadable(() => import('@/modules/tasks/pages/TasksPrintPage'));
const ControlTowerPage = lazyReloadable(() => import('@/modules/management/pages/ControlTowerPage'));
const ExceptionsPage = lazyReloadable(() => import('@/modules/management/pages/ExceptionsPage'));
const ManagementQueriesPage = lazyReloadable(() => import('@/modules/management/pages/ManagementQueriesPage'));
const TemporaryAccessPage = lazyReloadable(() => import('@/modules/management/pages/TemporaryAccessPage'));
const SystemAuditPage = lazyReloadable(() => import('@/modules/management/pages/SystemAuditPage'));
const ExceptionDetailPage = lazyReloadable(() => import('@/modules/management/pages/ExceptionDetailPage'));
const ManagementQueryDetailPage = lazyReloadable(() => import('@/modules/management/pages/ManagementQueryDetailPage'));
const TemporaryAccessDetailPage = lazyReloadable(() => import('@/modules/management/pages/TemporaryAccessDetailPage'));

/* -------------------------------------------------------------------------- */
/* Reports                                                                   */
/* -------------------------------------------------------------------------- */
const ReportingDashboard = lazyReloadable(() => import('@/modules/reporting/pages/ReportingDashboard'));
const BusinessHealthPage = lazyReloadable(() => import('@/modules/reports/pages/BusinessHealthPage'));
const ProfitLossPage = lazyReloadable(() => import('@/modules/reports/pages/ProfitLossPage'));
const CustomerAgingPage = lazyReloadable(() => import('@/modules/reports/pages/CustomerAgingPage'));
const SalesRegisterPage = lazyReloadable(() => import('@/modules/reports/pages/SalesRegisterPage'));
const PurchaseRegisterPage = lazyReloadable(() => import('@/modules/reports/pages/PurchaseRegisterPage'));
const StockValuationPage = lazyReloadable(() => import('@/modules/reports/pages/StockValuationPage'));
const WasteReport = lazyReloadable(() => import('@/modules/reports/pages/WasteReport'));
const SalesMarginReport = lazyReloadable(() => import('@/modules/reports/pages/SalesMarginReport'));
const RejectionsReport = lazyReloadable(() => import('@/modules/reports/pages/RejectionsReport'));
const BudgetVsActualReport = lazyReloadable(() => import('@/modules/reports/pages/BudgetVsActualReport'));

/* Catering MIS reports (§48) — operations, people, buying, bids and events. */
const DailySiteReportPage = lazyReloadable(() => import('@/modules/reports/pages/DailySiteReportPage'));
const ConsumptionReportPage = lazyReloadable(() => import('@/modules/reports/pages/ConsumptionReportPage'));
const WastageAnalysisPage = lazyReloadable(() => import('@/modules/reports/pages/WastageAnalysisPage'));
const ChecklistCompliancePage = lazyReloadable(() => import('@/modules/reports/pages/ChecklistCompliancePage'));
const AttendanceSummaryPage = lazyReloadable(() => import('@/modules/reports/pages/AttendanceSummaryPage'));
const ManpowerShortagePage = lazyReloadable(() => import('@/modules/reports/pages/ManpowerShortagePage'));
const WageCompliancePage = lazyReloadable(() => import('@/modules/reports/pages/WageCompliancePage'));
const VendorScorecardPage = lazyReloadable(() => import('@/modules/reports/pages/VendorScorecardPage'));
const RateChangeHistoryPage = lazyReloadable(() => import('@/modules/reports/pages/RateChangeHistoryPage'));
const PendingPurchaseOrdersPage = lazyReloadable(() => import('@/modules/reports/pages/PendingPurchaseOrdersPage'));
const TenderPipelinePage = lazyReloadable(() => import('@/modules/reports/pages/TenderPipelinePage'));
const EventProfitabilityPage = lazyReloadable(() => import('@/modules/reports/pages/EventProfitabilityPage'));

/* -------------------------------------------------------------------------- */
/* WhatsApp — every child renders inside the licence guard.                  */
/* -------------------------------------------------------------------------- */
const WhatsAppLayout = lazyReloadable(() => import('@/modules/whatsapp/features/whatsapp-layout'));
const WhatsAppDashboardPage = lazyReloadable(() => import('@/modules/whatsapp/pages/WhatsAppDashboardPage'));
const WhatsAppSetupPage = lazyReloadable(() => import('@/modules/whatsapp/pages/WhatsAppSetupPage'));
const WhatsAppSettingsPage = lazyReloadable(() => import('@/modules/whatsapp/pages/WhatsAppSettingsPage'));
const WhatsAppInboxPage = lazyReloadable(() => import('@/modules/whatsapp/pages/InboxPage'));
const WhatsAppContactsPage = lazyReloadable(() => import('@/modules/whatsapp/pages/ContactsPage'));
const WhatsAppContactDetailPage = lazyReloadable(() => import('@/modules/whatsapp/pages/ContactDetailPage'));
const WhatsAppContactGroupsPage = lazyReloadable(() => import('@/modules/whatsapp/pages/ContactGroupsPage'));
const WhatsAppContactGroupDetailPage = lazyReloadable(() => import('@/modules/whatsapp/pages/ContactGroupDetailPage'));
const WhatsAppTemplatesPage = lazyReloadable(() => import('@/modules/whatsapp/pages/TemplatesPage'));
const WhatsAppTemplateFormPage = lazyReloadable(() => import('@/modules/whatsapp/pages/TemplateFormPage'));
const WhatsAppCampaignsPage = lazyReloadable(() => import('@/modules/whatsapp/pages/CampaignsPage'));
const WhatsAppCampaignFormPage = lazyReloadable(() => import('@/modules/whatsapp/pages/CampaignFormPage'));
const WhatsAppCampaignDetailPage = lazyReloadable(() => import('@/modules/whatsapp/pages/CampaignDetailPage'));
const WhatsAppReportsPage = lazyReloadable(() => import('@/modules/whatsapp/pages/ReportsPage'));

/* Standalone print view — no AppShell chrome. */
const PrintPage = lazyReloadable(() => import('@/shared/features/printable/PrintPage'));

export const router = createBrowserRouter([
  {
    element: <GuestGuard><Outlet /></GuestGuard>,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/register', element: <RegisterPage /> },
      { path: '/forgot', element: <ForgotPage /> },
    ],
  },
  {
    element: <AuthGuard><Outlet /></AuthGuard>,
    children: [
      { path: '/print/:kind/:id', element: <PrintPage /> },
      // HR's own branded documents — advance voucher, loan sanction letter,
      // full-and-final statement, deduction statement, wage rate sheet.
      { path: '/print/hr/:kind/:id', element: <HrPrintPage /> },
      // Catering's own sheets — month-end statement, meal register, meal
      // record, pax certificate, pax register, site profile, site day sheet.
      { path: '/print/sites/:kind/:id', element: <SitesPrintPage /> },
      // Capital equipment on paper — the gate pass a security guard keeps when
      // a machine leaves a site, an asset's own record sheet, and the filtered
      // register a physical stock take is walked round on.
      { path: '/print/assets/:kind/:id', element: <AssetsPrintPage /> },
      // A contract and its renewal notice — the documents a client receives.
      { path: '/print/contract/:kind/:id', element: <ContractPrintPage /> },
      // The opening checklist that goes on a wall and into a client kick-off pack.
      { path: '/print/mobilization/:kind/:id', element: <MobilizationPrintPage /> },
      // The kitchen's paperwork — cycle menu wall chart, recipe card, cook
      // sheet, store indent, loss note, wastage register.
      { path: '/print/kitchen/:kind/:id', element: <KitchenPrintPage /> },
      // The bid desk's paper — bid approval sheet, costing build-up, pipeline,
      // deadline sheet, document-pack checklist.
      { path: '/print/tender/:kind/:id', element: <TenderPrintPage /> },
      // Payroll's paper — payroll register, bank transfer advice, cost
      // summary, payslip register, salary structure sheet and the two master
      // registers an auditor asks for.
      { path: '/print/payroll/:kind/:id', element: <PayrollPrintPage /> },
      // Buying paper the generic printable page does not carry — the vendor
      // quotation, the quote-comparison sheet filed with the approval, and the
      // twelve-month spend summary a manager takes into a cost meeting.
      { path: '/print/purchase/:kind/:id', element: <PurchasePrintPage /> },
      // Product sheet and item rate list. Static segments, so react-router
      // ranks them above the generic `/print/:kind/:id` regardless of order.
      { path: '/print/product/:id', element: <ProductPrintSheetPage /> },
      { path: '/print/product-price-list', element: <ProductPriceListPrintPage /> },
      // Billing's two reports on paper — the collections meeting's ageing
      // sheet, and one client's statement of account.
      { path: '/print/receivables-ageing', element: <ReceivablesAgeingPrintPage /> },
      { path: '/print/customer-statement/:customerId', element: <CustomerStatementPrintPage /> },
      // Accounting's statutory and audit paper — chart of accounts, signed
      // trial balance, journal register (day book), journal voucher, account
      // statement, bank position, budget vs actual, reconciliation statement.
      { path: '/print/accounting/:kind/:id', element: <AccountingPrintPage /> },
      // Expense paper: the claim voucher stapled to its receipts, and the cash
      // advance voucher the recipient signs.
      { path: '/print/expense/:kind/:id', element: <ExpensePrintPage /> },
      // Finance paper: the facility summary a bank asks for at renewal, and
      // the cash-flow forecast that goes into a board pack.
      // The morning shortage sheet a regional manager circulates at 8am, and
      // the requisition a labour supplier receives.
      { path: '/print/manpower/:kind/:id', element: <ManpowerPrintPage /> },
      // Work on paper — a supervisor's shift-start job sheet, one task with
      // its full history for a complaint file, a signed-off hygiene round,
      // and a site's day of compliance for an FSSAI or client audit.
      { path: '/print/tasks/:kind/:id', element: <TasksPrintPage /> },
      // Expense paper somebody signs — the claim voucher stapled to its
      // receipts, and the advance voucher against cash handed over.
      // Treasury paper — the cash-flow forecast for a bank or board pack, the
      // collections list, and a facility summary for a renewal.
    ],
  },
  {
    element: (
      <AuthGuard>
        <AppShell>
          <Outlet />
        </AppShell>
      </AuthGuard>
    ),
    children: [
      { path: '/', element: <RoleHomeRedirect /> },
      { path: '/dashboard', element: <DashboardPage /> },
      { path: '/profile', element: <ProfilePage /> },
      { path: '/notifications', element: <NotificationsPage /> },
      { path: '/approvals', element: <ApprovalsPage /> },
      { path: '/help', element: <HelpTourPage /> },
      { path: '/self-service', element: <SelfServicePage /> },

      /* ---- Management -------------------------------------------------- */
      { path: '/management/control-tower', element: <ControlTowerPage /> },
      { path: '/management/exceptions', element: <ExceptionsPage /> },
      { path: '/management/exceptions/:id', element: <ExceptionDetailPage /> },
      { path: '/management/queries', element: <ManagementQueriesPage /> },
      { path: '/management/queries/:id', element: <ManagementQueryDetailPage /> },
      { path: '/management/temporary-access', element: <TemporaryAccessPage /> },
      { path: '/management/temporary-access/:id', element: <TemporaryAccessDetailPage /> },
      { path: '/management/audit', element: <SystemAuditPage /> },

      /* ---- My work ----------------------------------------------------- */
      { path: '/my-work', element: <MyWorkPage /> },
      { path: '/tasks', element: <TasksPage /> },
      { path: '/tasks/:id', element: <TaskDetailPage /> },
      { path: '/checklists', element: <ChecklistRunsPage /> },
      { path: '/checklists/:id', element: <ChecklistRunPage /> },

      /* ---- Sites, meals, pax ------------------------------------------- */
      { path: '/sites', element: <SitesPage /> },
      { path: '/sites/pax-changes', element: <PaxChangesPage /> },
      { path: '/sites/pax-changes/:id', element: <PaxChangeDetailPage /> },
      { path: '/sites/meals', element: <MealServicesPage /> },
      { path: '/sites/meals/:id', element: <MealServiceDetailPage /> },
      { path: '/sites/reconciliations', element: <ReconciliationsPage /> },
      { path: '/sites/reconciliations/:id', element: <ReconciliationDetailPage /> },
      { path: '/sites/:id', element: <SiteDashboardPage /> },

      /* ---- Kitchen ----------------------------------------------------- */
      { path: '/kitchen/recipes', element: <RecipesPage /> },
      { path: '/kitchen/recipes/:id', element: <RecipeDetailPage /> },
      { path: '/kitchen/menus', element: <MenusPage /> },
      { path: '/kitchen/menus/:id', element: <MenuDetailPage /> },
      { path: '/kitchen/production', element: <ProductionPlansPage /> },
      { path: '/kitchen/production/:id', element: <ProductionPlanDetailPage /> },
      { path: '/kitchen/wastage', element: <WastagePage /> },
      { path: '/kitchen/wastage/:id', element: <WastageDetailPage /> },

      /* ---- Events, tenders, mobilization -------------------------------- */
      { path: '/events', element: <EventsPage /> },
      { path: '/events/closure', element: <EventClosureBoardPage /> },
      { path: '/events/types', element: <EventTypesPage /> },
      { path: '/events/types/:id', element: <EventTypeDetailPage /> },
      { path: '/events/:id', element: <EventDetailPage /> },
      { path: '/tenders', element: <TendersPage /> },
      { path: '/tenders/dashboard', element: <TenderDashboardPage /> },
      // Registered above `/tenders/:id` so these two are not read as a tender
      // with an id of "milestones" or "documents".
      { path: '/tenders/milestones', element: <TenderMilestonesPage /> },
      { path: '/tenders/documents', element: <TenderDocumentsPage /> },
      { path: '/tenders/:id', element: <TenderDetailPage /> },
      { path: '/tenders/:id/costings/:costingId', element: <TenderCostingDetailPage /> },
      { path: '/mobilizations', element: <MobilizationsPage /> },
      { path: '/mobilizations/:id', element: <MobilizationDetailPage /> },

      /* ---- Products + stores -------------------------------------------- */
      { path: '/products', element: <ProductsPage /> },
      { path: '/products/dashboard', element: <ProductDashboard /> },
      // Bulk + batch label sheets. Static segment, so it outranks
      // `/products/:id` — previously this fell through to the detail page and
      // rendered "Product not found" for every bulk print.
      { path: '/products/labels', element: <ProductLabelPage /> },
      // Type-scoped catalog entry points the list page pre-filters on.
      { path: '/products/raw-materials', element: <ProductsPage /> },
      { path: '/products/finished-goods', element: <ProductsPage /> },
      { path: '/products/:id', element: <ProductDetailPage /> },
      { path: '/products/:id/label', element: <ProductLabelPage /> },
      { path: '/inventory/dashboard', element: <InventoryDashboard /> },
      { path: '/inventory/stock', element: <StockBalancesPage /> },
      // A balance opens the movements that produced it.
      { path: '/inventory/stock/:productId/:warehouseId', element: <StockLedgerPage /> },
      { path: '/inventory/movements', element: <StockMovementsPage /> },
      { path: '/inventory/issues', element: <StockIssuesPage /> },
      { path: '/inventory/issues/:id', element: <StockIssueDetailPage /> },
      { path: '/inventory/aging', element: <StockAgingPage /> },
      { path: '/inventory/reorder', element: <ReorderSuggestionsPage /> },
      { path: '/inventory/cycle-count', element: <CycleCountPage /> },
      { path: '/inventory/counts', element: <StockCountsPage /> },
      { path: '/inventory/counts/:id', element: <StockCountDetailPage /> },
      { path: '/inventory/batches', element: <BatchesPage /> },
      { path: '/inventory/batches/:id', element: <BatchDetailPage /> },
      { path: '/inventory/transfers', element: <StockTransfersPage /> },
      { path: '/inventory/transfers/new', element: <StockTransferFormPage /> },
      { path: '/inventory/transfers/:id', element: <StockTransferDetailPage /> },
      { path: '/inventory/transfers/:id/edit', element: <StockTransferFormPage /> },
      { path: '/inventory/adjustments', element: <StockAdjustmentsPage /> },
      { path: '/inventory/adjustments/new', element: <StockAdjustmentFormPage /> },
      { path: '/inventory/adjustments/:id', element: <StockAdjustmentDetailPage /> },
      { path: '/inventory/adjustments/:id/edit', element: <StockAdjustmentFormPage /> },

      /* ---- Procurement -------------------------------------------------- */
      { path: '/purchase/dashboard', element: <PurchaseDashboard /> },
      { path: '/purchase/requests', element: <PurchaseRequestsPage /> },
      { path: '/purchase/requests/new', element: <PurchaseRequestFormPage /> },
      { path: '/purchase/requests/:id', element: <PurchaseRequestDetailPage /> },
      { path: '/purchase/requests/:id/edit', element: <PurchaseRequestFormPage /> },
      { path: '/purchase/orders', element: <PurchaseOrdersPage /> },
      { path: '/purchase/orders/new', element: <PurchaseOrderFormPage /> },
      { path: '/purchase/orders/:id', element: <PurchaseOrderDetailPage /> },
      { path: '/purchase/orders/:id/edit', element: <PurchaseOrderFormPage /> },
      { path: '/purchase/receipts', element: <GoodsReceiptsPage /> },
      { path: '/purchase/receipts/:id', element: <GoodsReceiptDetailPage /> },
      { path: '/purchase/bills', element: <BillsPage /> },
      { path: '/purchase/bills/new', element: <BillFormPage /> },
      { path: '/purchase/bills/:id', element: <BillDetailPage /> },
      { path: '/purchase/debit-notes', element: <DebitNotesPage /> },
      { path: '/purchase/debit-notes/:id', element: <DebitNoteDetailPage /> },
      { path: '/purchase/rfqs', element: <RfqsPage /> },
      { path: '/purchase/rfqs/new', element: <RfqFormPage /> },
      { path: '/purchase/rfqs/:id', element: <RfqDetailPage /> },
      { path: '/purchase/rfqs/:id/edit', element: <RfqFormPage /> },
      { path: '/purchase/rfqs/:id/compare', element: <RfqComparePage /> },
      { path: '/purchase/quotes', element: <SupplierQuotesPage /> },
      { path: '/purchase/quotes/new', element: <SupplierQuoteFormPage /> },
      { path: '/purchase/quotes/:id', element: <SupplierQuoteDetailPage /> },
      { path: '/purchase/supplier-evaluation', element: <SupplierEvaluationPage /> },

      /* ---- Billing ------------------------------------------------------ */
      { path: '/billing/dashboard', element: <BillingDashboardPage /> },
      { path: '/billing/invoices', element: <InvoicesPage /> },
      { path: '/billing/invoices/new', element: <SalesInvoiceFormPage /> },
      { path: '/billing/invoices/:id', element: <InvoiceDetailPage /> },
      // Editing a draft invoice: same form, hydrated from the id. Without it a
      // mis-keyed line could only be dealt with by cancelling and re-raising.
      { path: '/billing/invoices/:id/edit', element: <SalesInvoiceFormPage /> },
      { path: '/billing/credit-notes', element: <CreditNotesPage /> },
      { path: '/billing/credit-notes/:id', element: <CreditNoteDetailPage /> },
      { path: '/billing/receipts', element: <ReceiptsPage /> },
      { path: '/billing/receipts/:id', element: <ReceiptDetailPage /> },
      { path: '/billing/ageing', element: <ReceivablesAgeingPage /> },
      { path: '/billing/statements', element: <CustomerStatementPage /> },

      /* ---- People -------------------------------------------------------- */
      { path: '/hr/dashboard', element: <HrDashboard /> },
      { path: '/hr/employees', element: <EmployeesPage /> },
      { path: '/hr/employees/new', element: <EmployeeFormPage /> },
      // Ahead of `/hr/employees/:id` so the bulk badge sheet is not read as an
      // employee whose id is the literal string "id-cards".
      { path: '/hr/employees/id-cards', element: <EmployeeIdCardPage /> },
      { path: '/hr/employees/:id', element: <EmployeeDetailPage /> },
      { path: '/hr/employees/:id/edit', element: <EmployeeFormPage /> },
      { path: '/hr/employees/:id/id-card', element: <EmployeeIdCardPage /> },
      { path: '/hr/org-chart', element: <OrgChartPage /> },
      { path: '/hr/advances', element: <AdvancesPage /> },
      { path: '/hr/advances/:id', element: <AdvanceDetailPage /> },
      { path: '/hr/recoveries/due', element: <HrRecoveriesDuePage /> },
      { path: '/hr/recoveries/:id', element: <RecoveryDetailPage /> },
      { path: '/hr/recoveries', element: <RecoveriesPage /> },
      { path: '/hr/exits', element: <EmployeeExitsPage /> },
      { path: '/hr/exits/:id', element: <EmployeeExitDetailPage /> },
      { path: '/hr/loans', element: <HrLoansPage /> },
      { path: '/hr/loans/:id', element: <HrLoanDetailPage /> },
      { path: '/hr/id-cards', element: <HrIdCardsPage /> },
      { path: '/hr/id-cards/:id', element: <HrIdCardDetailPage /> },
      { path: '/hr/uniforms', element: <HrUniformIssuesPage /> },
      { path: '/hr/uniforms/:id', element: <HrUniformIssueDetailPage /> },
      { path: '/hr/uniform-items', element: <HrUniformItemsPage /> },
      { path: '/hr/wage-rules', element: <WageRulesPage /> },
      { path: '/hr/wage-rules/:id', element: <WageRuleDetailPage /> },
      { path: '/manpower/board', element: <ManpowerBoardPage /> },
      { path: '/manpower/requisitions', element: <RequisitionsPage /> },
      { path: '/manpower/requisitions/:id', element: <RequisitionDetailPage /> },
      { path: '/manpower/reserve-pool', element: <ReservePoolPage /> },
      { path: '/attendance/dashboard', element: <AttendanceDashboard /> },
      { path: '/attendance/list', element: <AttendanceListPage /> },
      { path: '/attendance/calendar', element: <AttendanceCalendarPage /> },
      { path: '/leave/dashboard', element: <LeaveDashboard /> },
      { path: '/leave/apply', element: <LeaveApplicationPage /> },
      { path: '/leave/approvals', element: <LeaveApprovalsPage /> },
      { path: '/leave/calendar', element: <TeamLeaveCalendarPage /> },
      { path: '/leave/requests/:id', element: <LeaveRequestDetailPage /> },
      { path: '/payroll/dashboard', element: <PayrollDashboard /> },
      { path: '/payroll/runs', element: <PayrollRunPage /> },
      { path: '/payroll/runs/:id', element: <PayrollRunDetailPage /> },
      { path: '/payroll/payslips', element: <PayslipsPage /> },
      { path: '/payroll/payslips/:id', element: <PayslipDetailPage /> },
      { path: '/payroll/salary-structures', element: <SalaryStructuresPage /> },
      { path: '/payroll/salary-structures/:id', element: <SalaryStructureDetailPage /> },
      { path: '/payroll/salary-components', element: <SalaryComponentsPage /> },
      { path: '/payroll/salary-components/:id', element: <SalaryComponentDetailPage /> },

      /* ---- Money --------------------------------------------------------- */
      { path: '/accounting/dashboard', element: <AccountingDashboard /> },
      { path: '/accounting/coa', element: <ChartOfAccountsPage /> },
      { path: '/accounting/coa/:id', element: <AccountLedgerPage /> },
      { path: '/accounting/journal', element: <JournalPage /> },
      { path: '/accounting/journal/new', element: <JournalEntryFormPage /> },
      { path: '/accounting/journal/:id', element: <JournalEntryDetailPage /> },
      { path: '/accounting/trial-balance', element: <TrialBalancePage /> },
      { path: '/accounting/bank-accounts', element: <BankAccountsPage /> },
      { path: '/accounting/bank-accounts/:id', element: <BankAccountDetailPage /> },
      { path: '/accounting/bank-reconciliation', element: <BankReconciliationPage /> },
      { path: '/accounting/budgets', element: <BudgetsPage /> },
      { path: '/accounting/budgets/new', element: <BudgetFormPage /> },
      { path: '/accounting/budgets/:id', element: <BudgetDetailPage /> },
      { path: '/accounting/budgets/:id/edit', element: <BudgetFormPage /> },
      { path: '/accounting/receipts-payments', element: <ReceiptsPaymentsPage /> },
      { path: '/accounting/tally-export', element: <TallyExportPage /> },
      { path: '/expenses/dashboard', element: <ExpenseDashboard /> },
      { path: '/expenses/claims', element: <ExpenseClaimsPage /> },
      { path: '/expenses/claims/new', element: <ExpenseClaimFormPage /> },
      { path: '/expenses/claims/:id', element: <ExpenseClaimDetailPage /> },
      { path: '/expenses/claims/:id/edit', element: <ExpenseClaimFormPage /> },
      { path: '/expenses/categories', element: <ExpenseCategoriesPage /> },
      { path: '/expenses/advances', element: <ExpenseAdvancesPage /> },
      { path: '/expenses/advances/:id', element: <ExpenseAdvanceDetailPage /> },
      { path: '/finance/facilities', element: <CreditFacilitiesPage /> },
      { path: '/finance/facilities/:id', element: <CreditFacilityDetailPage /> },
      { path: '/finance/cash-flow', element: <CashFlowPage /> },
      { path: '/finance/profit-vs-cash', element: <ProfitVsCashPage /> },

      /* ---- Quality, upkeep, contracts ------------------------------------ */
      { path: '/quality/dashboard', element: <QualityDashboard /> },
      { path: '/quality/complaints', element: <ComplaintsPage /> },
      { path: '/quality/food-safety', element: <FoodSafetyPage /> },
      { path: '/quality/inspections', element: <InspectionsPage /> },
      { path: '/quality/inspections/:id', element: <InspectionDetailPage /> },
      { path: '/quality/ncr', element: <NonConformancesPage /> },
      { path: '/quality/ncr/:id', element: <NonConformanceDetailPage /> },
      { path: '/assets', element: <AssetsPage /> },
      // Registered above `/assets/:id` so this is not read as an asset whose
      // id is the word "categories".
      { path: '/assets/categories', element: <AssetCategoriesPage /> },
      { path: '/assets/:id', element: <AssetDetailPage /> },
      { path: '/maintenance/dashboard', element: <MaintenanceDashboard /> },
      { path: '/maintenance/requests', element: <MaintenanceRequestsPage /> },
      { path: '/maintenance/requests/:id', element: <MaintenanceRequestDetailPage /> },
      { path: '/contracts', element: <ContractsPage /> },
      { path: '/contracts/dashboard', element: <ContractDashboard /> },
      // Static segment, registered above `/contracts/:id` so it is not read as
      // a contract with an id of "obligations".
      { path: '/contracts/obligations', element: <ContractObligationsPage /> },
      { path: '/contracts/:id', element: <ContractDetailPage /> },
      { path: '/compliance/dashboard', element: <ComplianceDashboard /> },
      { path: '/compliance/documents', element: <ComplianceDocumentsPage /> },
      { path: '/compliance/documents/:id', element: <ComplianceDocumentDetailPage /> },

      /* ---- Reports -------------------------------------------------------- */
      { path: '/reports', element: <ReportingDashboard /> },
      { path: '/reports/business-health', element: <BusinessHealthPage /> },
      { path: '/reports/pnl', element: <ProfitLossPage /> },
      { path: '/reports/customer-aging', element: <CustomerAgingPage /> },
      { path: '/reports/sales-register', element: <SalesRegisterPage /> },
      { path: '/reports/purchase-register', element: <PurchaseRegisterPage /> },
      { path: '/reports/stock-valuation', element: <StockValuationPage /> },
      { path: '/reports/waste', element: <WasteReport /> },
      { path: '/reports/margin', element: <SalesMarginReport /> },
      { path: '/reports/rejections', element: <RejectionsReport /> },
      { path: '/reports/budget-vs-actual', element: <BudgetVsActualReport /> },

      /* ---- Catering MIS reports (§48) ------------------------------------- */
      { path: '/reports/ops/daily-site', element: <DailySiteReportPage /> },
      { path: '/reports/ops/consumption', element: <ConsumptionReportPage /> },
      { path: '/reports/ops/wastage-analysis', element: <WastageAnalysisPage /> },
      { path: '/reports/ops/checklist-compliance', element: <ChecklistCompliancePage /> },
      { path: '/reports/hr/attendance-summary', element: <AttendanceSummaryPage /> },
      { path: '/reports/hr/manpower-shortage', element: <ManpowerShortagePage /> },
      { path: '/reports/hr/wage-compliance', element: <WageCompliancePage /> },
      { path: '/reports/procurement/vendor-scorecard', element: <VendorScorecardPage /> },
      { path: '/reports/procurement/rate-history', element: <RateChangeHistoryPage /> },
      { path: '/reports/procurement/pending-pos', element: <PendingPurchaseOrdersPage /> },
      { path: '/reports/tender/pipeline', element: <TenderPipelinePage /> },
      { path: '/reports/events/register', element: <EventProfitabilityPage /> },

      /* ---- WhatsApp ------------------------------------------------------- */
      {
        element: <WhatsAppLayout />,
        children: [
          { path: '/whatsapp', element: <WhatsAppDashboardPage /> },
          { path: '/whatsapp/setup', element: <WhatsAppSetupPage /> },
          { path: '/whatsapp/inbox', element: <WhatsAppInboxPage /> },
          { path: '/whatsapp/contacts', element: <WhatsAppContactsPage /> },
          { path: '/whatsapp/contacts/:id', element: <WhatsAppContactDetailPage /> },
          { path: '/whatsapp/groups', element: <WhatsAppContactGroupsPage /> },
          { path: '/whatsapp/groups/:id', element: <WhatsAppContactGroupDetailPage /> },
          { path: '/whatsapp/templates', element: <WhatsAppTemplatesPage /> },
          { path: '/whatsapp/templates/new', element: <WhatsAppTemplateFormPage /> },
          { path: '/whatsapp/campaigns', element: <WhatsAppCampaignsPage /> },
          { path: '/whatsapp/campaigns/new', element: <WhatsAppCampaignFormPage /> },
          { path: '/whatsapp/campaigns/:id', element: <WhatsAppCampaignDetailPage /> },
          { path: '/whatsapp/campaigns/:id/edit', element: <WhatsAppCampaignFormPage /> },
          { path: '/whatsapp/reports', element: <WhatsAppReportsPage /> },
          { path: '/whatsapp/settings', element: <WhatsAppSettingsPage /> },
        ],
      },

      /* ---- Setup ---------------------------------------------------------- */
      { path: '/setup/companies', element: <CompaniesPage /> },
      { path: '/setup/branches', element: <BranchesPage /> },
      { path: '/setup/departments', element: <DepartmentsPage /> },
      { path: '/setup/departments/:id', element: <DepartmentDetailPage /> },
      { path: '/setup/users', element: <UsersRolesPage /> },
      { path: '/setup/users/invite', element: <UserInvitePage /> },
      { path: '/setup/sessions', element: <SessionsPage /> },
      { path: '/setup/login-history', element: <LoginHistoryPage /> },
      { path: '/setup/numbering', element: <NumberingSettingsPage /> },
      { path: '/setup/global-settings', element: <GlobalSettingsPage /> },
      { path: '/setup/terminology', element: <TerminologyPage /> },
      { path: '/setup/import', element: <ImportPage /> },
      { path: '/setup/audit-log', element: <AuditLogPage /> },
      { path: '/setup/demo-data', element: <DemoDataPage /> },
      { path: '/masters/customers', element: <CustomersPage /> },
      { path: '/masters/customers/:id', element: <CustomerDetailPage /> },
      { path: '/masters/suppliers', element: <SuppliersPage /> },
      { path: '/masters/suppliers/:id', element: <SupplierDetailPage /> },
      { path: '/masters/warehouses', element: <WarehousesPage /> },
      { path: '/masters/warehouses/:id', element: <WarehouseDetailPage /> },
      { path: '/masters/taxes', element: <TaxesPage /> },
      { path: '/masters/payment-terms', element: <PaymentTermsPage /> },
      { path: '/masters/uoms', element: <UomsPage /> },
      { path: '/masters/categories', element: <CategoriesPage /> },
      { path: '/masters/currencies', element: <CurrenciesPage /> },
      { path: '/masters/transporters', element: <TransportersPage /> },

      /* ---- Aliases for paths this app used before the catering rebuild -- */
      ...legacyRedirectRoutes,

      /* A link that resolves to nothing gets a readable page rather than
         React Router's developer error screen. */
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
